import { useState } from "react";
import type { MouseEvent } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Icon } from "@/lib/icons";
import type { ModeratorTestItem } from "@/components/get-paid/ModeratorTestCard";
import type { useGetPaid } from "../useGetPaid";

type Props = {
  test: ModeratorTestItem;
  t: ReturnType<typeof useGetPaid>["t"];
  onConfirm: (test: ModeratorTestItem, reason: string) => Promise<boolean>;
};

export function TestSubmissionTrashButton({ test, t, onConfirm }: Props) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const name = test.userName || test.userId || test.title || test.id;
  const description = t.getPaid.testTrashDescription
    .replace("{name}", name)
    .replace("{language}", test.language || "-")
    .replace("{status}", test.status || "-");

  const confirm = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (reason.trim().length < 10 || busy) return;
    setBusy(true);
    try {
      const trashed = await onConfirm(test, reason.trim());
      if (trashed) {
        setReason("");
        setOpen(false);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setReason("");
      }}
    >
      <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <Icon name="trash2" className="h-3.5 w-3.5" />
        {t.getPaid.testTrashConfirm}
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t.getPaid.testTrashTitle}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          aria-label={t.getPaid.testTrashReason}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t.getPaid.testTrashReason}
          maxLength={500}
        />
        <AlertDialogFooter>
          <AlertDialogCancel>{t.getPaid.cancel}</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy || reason.trim().length < 10}
            onClick={confirm}
          >
            {busy ? t.getPaid.loading : t.getPaid.testTrashConfirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
